import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useFormContext } from "react-hook-form";
import refreshIconMagenta from "../../assets/icons/refresh-icon-magenta.svg";
import tooltipIcon from "../../assets/icons/tooltip-icon.svg";
import { useCreateSegmentList } from "../../services/segment/index";
import CustomTooltip from "../../shared/components/custom-tooltip/CustomTooltip";
import formatCreateSegmentPayload from "../../utils/formatCreateSegmentPayload";
import { BorderSecondary, CircularProgressCustom } from "../../utils/muiCustomTheme";
import { Insight, InsightsWrapProps } from "./segment-interface";
import { useCreateCampaign } from "../../services/campaign/index";
import { formatNumber } from "../../utils/formatNumber";

const createInsightItems = (
  insights?: Insight[],
  existingValues?: Record<string, Insight["value"]>
): Insight[] => [
  {
    key: "uniqueConsumers",
    label: "Unique Consumers",
    value: insights?.[0]?.value ?? existingValues?.uniqueConsumers ?? 0,
    requiresRefresh: true,
    tooltip:
      "Total number of distinct consumers associated with specific Golden_IDs included in the selection.",
  },
  {
    key: "engagedConsumers",
    label: "Engaged Consumers",
    value: insights?.[1]?.value ?? existingValues?.engagedConsumers ?? 0,
    requiresRefresh: true,
    tooltip:
      "Number of unique consumers associated with specific Golden_IDs included in the selection that can be contacted or targeted (have valid email, phone, or address) and actively interacted in the last 12 months by e.g. making purchase, email opens or clicks.",
  },
  {
    key: "reachableConsumers",
    label: "Reachable Consumers",
    value: insights?.[2]?.value ?? existingValues?.reachableConsumers ?? 0,
    requiresRefresh: true,
    tooltip:
      "Number of unique consumers associated with specific Golden_IDs included in the selection that can be contacted or targeted. These consumers have valid and actionable contact information for email, phone, or address.",
  },
  {
    key: "uniqueEmails",
    label: "Unique Emails",
    value: insights?.[3]?.value ?? existingValues?.uniqueEmails ?? 0,
    requiresRefresh: true,
    tooltip:
      "Number of distinct normalized email addresses across the selected consumers.",
  },
];

function InsightsWrap({ title, hideRefresh = false, insights, dataSegment, campaignRefreshData }: InsightsWrapProps) {
  const methods = useFormContext();
  const { handleSubmit, getValues } = methods;
  const queryClient = useQueryClient();

  const [stateInsightsData, setStateInsightsData] = useState<Insight[]>(createInsightItems(insights));

  useEffect(() => {
    setStateInsightsData((current: Insight[]) =>
      createInsightItems(
        insights,
        Object.fromEntries(current.map((item: Insight) => [item.key, item.value]))
      )
    );
  }, [insights]);

  const { mutate: createCampaign, isPending: isPendingCreateCampaign } = useCreateCampaign({
    options: {
      onSuccess: async (res: any, variables: any) => {
        if (res?.data) {
          const keyMap: Record<string, keyof typeof res.data> = {
            uniqueConsumers: "count",
            engagedConsumers: "engaged",
            reachableConsumers: "reachable",
            uniqueEmails: "uniqueEmails",
          };
          const temp = stateInsightsData.map((item: Insight) => ({
            ...item,
            value:
              res?.data?.[keyMap[item.key]] !== undefined
                ? res.data[keyMap[item.key]]
                : item.value,
          }));
          setStateInsightsData(temp);

          if (variables?.type === "check" && variables?.campaignId) {
            await queryClient.invalidateQueries({
              queryKey: ["fetch-campaign"],
            });
          }
        }
      },
    },
  });

  const { mutate: createSegment, isPending: isPendingGetInsight } = useCreateSegmentList({
    options: {
      onSuccess: async (res: any, variables: any) => {
        if (res?.data) {
          const keyMap: Record<string, keyof typeof res.data> = {
            uniqueConsumers: "count",
            engagedConsumers: "engaged",
            reachableConsumers: "reachable",
            uniqueEmails: "uniqueEmails",
          };

          const temp = stateInsightsData.map((item: Insight) => ({
            ...item,
            value: res?.data?.[keyMap[item.key]] ?? item.value,
          }));
          setStateInsightsData(temp);

          if (variables?.type === "check" && variables?.segmentId) {
            await queryClient.invalidateQueries({
              queryKey: ["segment-detail"],
            });
          }
        }
      },
    },
  });

  const insightsData =  stateInsightsData;

  const parseSelectedPath = (path: string) => {
    const [country, brand, folder] = (path || "").split("/");
    return { country, brand, folder };
  };

  const handleRefreshInsight = (data: any) => {
    if (title === "Campaign insights:") {
      const {
        campaignId,
        endDate,
        startDate,
        timeValue,
        include,
        exclude,
        audienceGroups,
        campaignName,
        quantityValue,
        afterSpecificNumberOfOccurrences,
        path,
        user,
        campaignType,
      } = campaignRefreshData || data;

      const parsed = parseSelectedPath(path);

      const tempInclude = include?.[0]?.segmentId
        ? include
        : dataSegment?.data.filter((item: any) => include.includes(item?.segmentId));
      const tempExclude = exclude?.[0]?.segmentId
        ? exclude
        : dataSegment?.data.filter((item: any) => exclude.includes(item?.segmentId));

      const tempAudienceGroups = audienceGroups.map((item: any) => ({
        ...item,
        splitPercentage: Number(item.splitPercentage),
      }));

      const payload = {
        campaignId,
        campaignName,
        campaignType,
        include: tempInclude,
        exclude: tempExclude,
        audienceGroups: tempAudienceGroups,
        format: "JSON",
        quantityValue: Number(quantityValue),
        country: parsed?.country,
        folder: parsed?.folder,
        brand: parsed?.brand,
        afterSpecificNumberOfOccurrences,
        timeValue,
        endDate,
        startDate,
        user,
        type: "check"
      };

      createCampaign(payload);
    } else {
      const { sections, exclusions, user, country, segmentId, segmentName, path } = data;
      const parsed = parseSelectedPath(path);

      createSegment(
        formatCreateSegmentPayload({
          country: parsed?.country || country,
          sections,
          exclusions,
          user,
          brand: parsed?.brand,
          folder: parsed?.folder,
          segmentId,
          segmentName,
          pSegmentName:"",
          type: "check",

        })
      );
    }
  };

  const handleRefreshInsightsClick = () => {
    const data = getValues();
    const hasAtLeastOneCondition = data?.sections?.some(
      (section: any) => (section?.conditions?.length ?? 0) > 0
    );
    if (!hasAtLeastOneCondition) return;
    handleRefreshInsight(data);
  };

  return (
    <div className="create-segment__insights-wrap">
      <div className="create-segment__insights-wrap__header-wrap">
        <div className="create-segment__insights-wrap__head">{title}</div>
        {!hideRefresh && (
          <BorderSecondary
            loading={isPendingGetInsight || isPendingCreateCampaign}
            onClick={
              title === "Campaign insights:"
                ? campaignRefreshData
                  ? () => handleRefreshInsight(campaignRefreshData)
                  : handleSubmit(handleRefreshInsight)
                : handleRefreshInsightsClick
            }
            type="button"
            className="group"
          >
            Refresh insights
            <img
              className="btn-border__icon group-hover:opacity-70"
              src={refreshIconMagenta}
              alt="Refresh Icon"
            />
          </BorderSecondary>
        )}
      </div>

      <div className="create-segment__insights-wrap__value-wrap">
        {insightsData.map((item: Insight, index: number) => (
          <React.Fragment key={item.key}>
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "flex-start",
              }}
            >
              <div className="create-segment__insights-wrap__value-wrap__label">
                {item.label}
                {item.tooltip && (
                  <CustomTooltip title={item.tooltip}>
                    <img src={tooltipIcon} alt="Tooltip Icon" style={{ cursor: "pointer" }} />
                  </CustomTooltip>
                )}
              </div>

              {item.requiresRefresh && !hideRefresh ? (
                isPendingGetInsight || isPendingCreateCampaign ? (
                  <CircularProgressCustom size={24} />
                ) : (
                  <div className="create-segment__insights-wrap__value-wrap__val">
                    {formatNumber(item.value)}
                  </div>
                )
              ) : (
                <div className="create-segment__insights-wrap__value-wrap__val">
                  {formatNumber(item.value)}
                </div>
              )}
            </div>

            {index < insightsData.length - 1 && (
              <div
                style={{
                  width: "1px",
                  alignSelf: "stretch",
                  backgroundColor: "#EEEEEE",
                }}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

export default InsightsWrap;
